import {
  groupKitDocumentControllerCreateDocument,
  groupKitDocumentControllerDeleteDocument,
  groupKitDocumentControllerGetDocument,
  groupKitDocumentControllerGetDocuments,
  groupKitDocumentControllerUpdateDocument,
  groupKitDocumentControllerUpdateOrder,
  type GroupKitDocumentControllerCreateDocumentData,
  type GroupKitDocumentControllerUpdateDocumentData,
} from "@/lib/gen-api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import { queryKeys } from "../lib/query-keys";

const DOCUMENT_POLLING_INTERVAL = 5000;

export type TiptapContent =
  GroupKitDocumentControllerUpdateDocumentData["body"]["tiptapContent"];

export const useDocumentsData = (
  groupUuid: string | null,
  kitUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: documents,
    isPending,
    error,
    refetch,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.kitDocuments(groupUuid!, kitUuid!),
    queryFn: () =>
      groupKitDocumentControllerGetDocuments({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => res.data?.data ?? []),
  });

  const { mutateAsync: createDocument, isPending: isCreatingDocument } =
    useMutation({
      mutationFn: (
        vars: Partial<GroupKitDocumentControllerCreateDocumentData["body"]>,
      ) => {
        if (!groupUuid || !kitUuid) {
          throw new Error("Group UUID and Kit UUID are required");
        }

        return groupKitDocumentControllerCreateDocument({
          path: {
            groupUuid,
            kitUuid,
          },
          body: {
            ...vars,
            name: vars.name || "Untitled Document",
          },
        }).then((res) => res.data);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitDocuments(groupUuid!, kitUuid!),
        });
      },
    });

  const { mutateAsync: deleteDocument, isPending: isDeletingDocument } =
    useMutation({
      mutationFn: async (documentUuid: string) => {
        if (!groupUuid || !kitUuid) {
          throw new Error("Group UUID and Kit UUID are required");
        }

        queryClient.setQueryData<{ uuid: string }[]>(
          queryKeys.kitDocuments(groupUuid, kitUuid),
          (old) => {
            if (!old) return old;
            return old.filter((d) => d.uuid !== documentUuid);
          },
        );

        return groupKitDocumentControllerDeleteDocument({
          path: {
            groupUuid,
            kitUuid,
            documentUuid,
          },
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitDocuments(groupUuid!, kitUuid!),
        });
      },
    });

  const { mutateAsync: reorderDocuments, isPending: isReorderingDocuments } =
    useMutation({
      mutationFn: async (vars: { uuids: string[] }) => {
        if (!groupUuid || !kitUuid) {
          throw new Error(
            "Group UUID and Kit UUID are required for reordering documents",
          );
        }
        const documents = await groupKitDocumentControllerUpdateOrder({
          path: {
            groupUuid,
            kitUuid,
          },
          body: {
            documentUuids: vars.uuids,
          },
        });
        return documents;
      },
      onSettled: () => {
        // Invalidate both kit-specific and group-wide document queries
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitDocuments(groupUuid!, kitUuid!),
        });
      },
    });

  return {
    documents: documents || [],
    isLoading: isPending,
    isMutating: isCreatingDocument || isDeletingDocument,
    error,
    createDocument,
    isCreatingDocument,
    deleteDocument,
    isDeletingDocument,
    refetchDocuments: refetch,
    reorderDocuments,
    isReorderingDocuments,
  };
};

export function useDocumentData(
  groupUuid: string | null,
  kitUuid: string | null,
  documentUuid: string | null,
  options?: {
    polling?: boolean;
  },
) {
  const queryClient = useQueryClient();

  const { data: document, isLoading } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!documentUuid,
    queryKey: queryKeys.kitDocument(groupUuid!, kitUuid!, documentUuid!),
    queryFn: () =>
      groupKitDocumentControllerGetDocument({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          documentUuid: documentUuid!,
        },
      }).then((res) => res.data || null),
    refetchInterval: (data) => {
      if (!options?.polling) {
        return false;
      }

      if (data.state.data?.status === "generating") {
        return DOCUMENT_POLLING_INTERVAL;
      }
      return false;
    },
  });

  const { mutateAsync: updateDocument } = useMutation({
    mutationFn: (vars: {
      name?: string;
      tiptapContent?: TiptapContent;
      templateUuid?: string;
    }) => {
      if (!groupUuid || !kitUuid || !documentUuid) {
        throw new Error("Group UUID, Kit UUID, and Document UUID are required");
      }

      return groupKitDocumentControllerUpdateDocument({
        path: {
          groupUuid,
          kitUuid,
          documentUuid,
        },
        body: {
          name: vars.name,
          tiptapContent: vars.tiptapContent,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitDocuments(groupUuid!, kitUuid!),
      });

      // Also invalidate individual document
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitDocument(groupUuid!, kitUuid!, documentUuid!),
      });
    },
  });

  const { mutateAsync: deleteDocument, isPending: isDeletingDocument } =
    useMutation({
      mutationFn: async () => {
        if (!groupUuid || !kitUuid || !documentUuid) {
          throw new Error(
            "Group UUID, Kit UUID, and Document UUID are required",
          );
        }

        await groupKitDocumentControllerDeleteDocument({
          path: {
            groupUuid,
            kitUuid,
            documentUuid,
          },
        });

        // Update the cache after a successful delete so navigation (which
        // awaits this mutation) doesn't see the deleted document. Unlike
        // invalidateQueries, this is synchronous — no refetch race.
        queryClient.setQueryData<{ uuid: string }[]>(
          queryKeys.kitDocuments(groupUuid, kitUuid),
          (old) => {
            if (!old) return old;
            return old.filter((d) => d.uuid !== documentUuid);
          },
        );
        queryClient.removeQueries({
          queryKey: queryKeys.kitDocument(groupUuid, kitUuid, documentUuid),
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitDocuments(groupUuid!, kitUuid!),
        });
      },
    });

  return {
    document,
    isLoading,
    updateDocument,
    deleteDocument,
    isDeletingDocument,
  };
}
